import React, { useState, useEffect } from "react";

//CSS
import "./tagPopups.css";
import "../0PageStyles.css";

//Components
import ColorCircle from "../../Components/Tag Components/colorCircle";
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

//Functions and hooks
import { GetTagData, EditTag, DeleteTag } from "../../BackendAccess/TagHooks";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";
import useFormState from "../../hooks/useFormStates";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function TagEditPopup({
  currentID,
  onCancel,
  onEdit,
  onDelete,
}) {
  const {
    formData,
    setField,
    loadInitialData,
    hasChanged: bCanSaveChanges,
  } = useFormState({});

  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(false);

  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError, false);

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const showDeletePopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style="warning"
        message={
          <p>
            {t("Are you sure you want to delete this tag?", { ns: "tags" })}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => {
          handleConfirmDelete(key);
        }}
        onCancel={() => {
          handleCancelDelete(key);
        }}
      />,
      key
    );
  };

  //////// Load Data ///////////////////////////////////////
  const GetAndLoadData = async () => {
    try {
      setLoading(true);
      const [info, error] = await GetTagData(currentID);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        LoadData(info);
      }
    } catch (err) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const LoadData = (tag) => {
    loadInitialData({
      ...tag,
      LastEditedBy:
        tag.FirstName + " " + tag.LastName + " (" + tag.LastEditedBy + ")",
    });
  };

  useEffect(() => {
    if (currentID) {
      GetAndLoadData();
    }
  }, [currentID]);

  //////// Change Handlers ///////////////////////////////////////
  const handleChange = (field, value) => {
    setField(field, value);
  };

  //////// Save Changes ///////////////////////////////////////
  const OnConfirmChanges = async () => {
    showNotification(t("Editing tag...", { ns: "tags" }), "info");
    setEditing(true);
    try {
      const [data, error] = await EditTag(
        currentID,
        formData.TagID,
        formData.Name,
        formData.Color,
        formData.Description
      );
      if (error) {
        setError(error);
        return;
      }
      showNotification(
        t("Tag edited successfully!", { ns: "tags" }),
        "success"
      );
      onCancel();
      await onEdit();
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setEditing(false);
    }
  };

  //////// Delete ///////////////////////////////////////
  const clickOnDelete = () => {
    showDeletePopup();
  };
  const handleCancelDelete = (key) => {
    removePopup(key);
  };

  const handleConfirmDelete = async (key) => {
    showNotification(t("Deleting tag...", { ns: "tags" }), "info");
    setDeleteBtnDisabled(true);
    try {
      const [data, error] = await DeleteTag(currentID);
      if (error) {
        setError(error);
        return;
      }
      showNotification(
        t("Tag deleted successfully!", { ns: "tags" }),
        "success"
      );
      onDelete();
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
      removePopup(key);
    }
  };

  const clickOnCancel = () => {
    showNotification(t("Tag editing cancelled.", { ns: "tags" }), "info");
    onCancel();
  };

  return (
    <div className="input-popup">
      <div className="dimming-cover" />
      <div className="input-popup-container">
        <PageLoader loading={loading} error={pageError}>
          <div className="input-popup-fields-container tags">
            <InputField
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={formData.TagID || ""}
              onChange={(val) => handleChange("TagID", val)}
              mandatory={true}
              readOnly={true}
            />
            <InputField
              placeholder={t("Example: Metal items", { ns: "tags" })}
              label={t("Name") + ":"}
              value={formData.Name || ""}
              onChange={(val) => handleChange("Name", val)}
              mandatory={true}
            />
            <ColorCircle
              color={formData.Color || ""}
              onChange={(val) => handleChange("Color", val)}
            />
          </div>
          <div className="input-popup-fields-container tags-description">
            <InputField
              className="mb-0"
              placeholder={t(
                "Example: The items with this tag are made out of metal.",
                { ns: "tags" }
              )}
              label={t("Description") + ":"}
              value={formData.Description || ""}
              onChange={(val) => handleChange("Description", val)}
            />
          </div>
          <MandatoryExplanation />

          <div className="btn-container">
            <Button
              btnMargin="btn-default"
              btnStyle="btn-positive"
              onClick={OnConfirmChanges}
              disabled={!bCanSaveChanges || editing}
              acceptEnter={true}
              icon="apply"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin="btn-default"
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin="btn-default"
              btnStyle="btn-negative"
              onClick={clickOnDelete}
              icon="delete"
              disabled={deleteBtnDisabled}
            >
              {t("Delete")}
            </Button>
          </div>
          <span className="last-edited-by-bar w-100% mt-0 mb-0">
            <span className="sec-textcolor-span ">{t("Last edited by:")} </span>
            &nbsp;
            {formData.LastEditedBy}
          </span>
        </PageLoader>
      </div>
    </div>
  );
}
